import { ColorPicker } from "@comps/ui/ColorPicker";
import { Button } from "@comps/ui/button";
import { Label } from "@comps/ui/label";
import { Textarea } from "@comps/ui/textarea";
import { TextData, TextWidgetType, Widget } from "@dto/Dashboard";
import { GRAY_200_COLOR, GRAY_600_COLOR, WorkspaceColorType } from "@utils/colors";

import { ReactComponent as ListOfBulletsSVG } from "@img/list-of-bullets.svg";
import { TW_WidgetSectionLabel, TW_WidgetSpecificSection, getWsButtonStyle, useHover } from "@utils/twClasses";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { WidgetCardConfigProps } from "./WidgetConfig";

export function TextCardConfig({ initialWidget, onConfigChange }: WidgetCardConfigProps) {
    const { t } = useTranslation();
    const [markdown, setMarkdown] = useState<string>((initialWidget?.data as TextData)?.markdown ?? "");
    const [selectedColor, setSelectedColor] = useState<string>("#ef4444");
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const hoverPoliceColor = useHover(getWsButtonStyle("#ffffff"), getWsButtonStyle(GRAY_200_COLOR));

    useEffect(() => {
        const newWidget = buildWidget(markdown);
        onConfigChange(newWidget);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markdown]);

    function buildWidget(md: string): Widget {
        return {
            type: TextWidgetType,
            title: initialWidget?.title ?? t("dashboard.widget.default.label.TEXT"),
            description: initialWidget?.description ?? "",
            width: initialWidget?.width ?? null,
            height: initialWidget?.height ?? null,
            showLegend: false,
            showValues: false,
            showPercentage: false,
            hideYAxis: false,
            data: new TextData(md),
        };
    }

    function wrapSelection(before: string, after: string) {
        const textarea = textareaRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selected = markdown.slice(start, end);
        const newMd = markdown.slice(0, start) + before + selected + after + markdown.slice(end);
        setMarkdown(newMd);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + before.length, end + before.length);
        }, 0);
    }

    function prefixLine(prefix: string) {
        const textarea = textareaRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const lineStart = markdown.lastIndexOf("\n", start - 1) + 1;
        const newMd = markdown.slice(0, lineStart) + prefix + markdown.slice(lineStart);
        setMarkdown(newMd);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + prefix.length, start + prefix.length);
        }, 0);
    }

    const colorButtonStyle = {
        color: selectedColor,
        ...hoverPoliceColor,
    }

    return (
        <div className={TW_WidgetSpecificSection}>
            <Label className={TW_WidgetSectionLabel}>{t("dashboard.widget.text.markdown.label")}</Label>
            <div className="flex flex-row gap-1 flex-wrap">
                <Button type="button" variant="outline" size="sm" onClick={() => prefixLine("# ")}>H1</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => prefixLine("## ")}>H2</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => prefixLine("### ")}>H3</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => wrapSelection("**", "**")}><strong>B</strong></Button>
                <Button type="button" variant="outline" size="sm" onClick={() => wrapSelection("_", "_")}><em>I</em></Button>
                <div className="flex flex-row gap-0 border-1 border-gray-200 rounded-md pr-0.5">
                    <Button type="button" size="sm" className="shadow-none px-2 mx-0.5 my-0.5 hover:bg-gray-200" {...colorButtonStyle} onClick={() => wrapSelection(`<span style="color: ${selectedColor}">`, "</span>")}>A</Button>
                    <ColorPicker className="w-2 h-5 hover:opacity-80" color={selectedColor} onChange={setSelectedColor} colorType={WorkspaceColorType} />
                </div>
                <ListOfBulletsSVG type="button" className="border-1 rounded-md py-0.5 px-1 hover:bg-gray-200 w-8 h-8" color={GRAY_600_COLOR} onClick={() => prefixLine("- ")} />
            </div>
            <Textarea
                ref={textareaRef}
                className="min-h-[150px] font-mono text-sm"
                placeholder={t("dashboard.widget.text.markdown.placeholder")}
                value={markdown}
                onChange={(e) => setMarkdown(e.target.value)}
            />
        </div>
    );
}
